import moment from "moment";
import { useEffect, useState } from "react";
import { API_URL, REACT_APP_SITE_KEY } from "../../config/app-config";
import calendar from "../../img/calendar.png";
import ReCAPTCHA from "react-google-recaptcha";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { FiArrowRight, FiSun, FiDownload, FiLock, FiCalendar, FiClock, FiZap } from "react-icons/fi";
import { FaStar, FaFacebookF, FaInstagram, FaLinkedinIn, FaYoutube, FaGlobe } from "react-icons/fa";

import "./ServiceCardThemeV5.css";

moment.locale("en");

// V3 Calendar Logic Component
function MonthGridCalendar({ slots, packageDays, selectedValue, onSelect }) {
  const today = moment().locale("en");
  const [viewMonth, setViewMonth] = useState(today.clone().startOf("month"));

  const availableSet = new Set((slots || []).filter((s) => s.available).map((s) => s.value));
  const slotMap = {};
  (slots || []).forEach((s) => { slotMap[s.value] = s; });

  const goMonth = (dir) => setViewMonth((prev) => prev.clone().locale("en").add(dir, "month").startOf("month"));
  
  const vmEn = viewMonth.clone().locale("en");
  const startDay = vmEn.clone().startOf("month").day();
  const daysInMonth = vmEn.daysInMonth();

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));

  const DOW = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const isPrevDisabled = viewMonth.clone().locale("en").isSameOrBefore(today.clone().startOf("month"));

  return (
    <div>
      <div className="monthNavRow" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <button type="button" onClick={() => goMonth(-1)} disabled={isPrevDisabled} style={{ background: 'none', border: 'none', cursor: isPrevDisabled ? 'not-allowed' : 'pointer', fontSize: '18px', color: 'var(--v5-dark)' }}>‹</button>
        <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>{vmEn.format("MMMM YYYY")}</h4>
        <button type="button" onClick={() => goMonth(1)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px', color: 'var(--v5-dark)' }}>›</button>
      </div>
      <table className="monthGridCalendar" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'center' }}>
        <thead>
          <tr>{DOW.map((d) => (<th key={d} style={{ padding: '8px 0', fontSize: '12px', opacity: 0.7 }}>{d}</th>))}</tr>
        </thead>
        <tbody>
          {weeks.map((week, wi) => (
            <tr key={wi}>
              {week.map((day, di) => {
                if (!day) return <td key={di}><div style={{ padding: '8px' }}></div></td>;
                const dateVal = vmEn.clone().date(day).format("YYYY/MM/DD");
                const isPast = vmEn.clone().date(day).isBefore(today, "day");
                const dayMatches = packageDays.length === 0 || packageDays.includes(moment(dateVal, "YYYY/MM/DD").locale("en").format("ddd").toUpperCase());
                const isAvail = availableSet.has(dateVal) && !isPast && dayMatches;
                const isSelected = selectedValue === dateVal;
                
                let btnClass = "gridDayBtn ";
                if(isSelected) btnClass += "gridSelected";
                else if(isAvail) btnClass += "gridAvailable";
                
                return (
                  <td key={di} style={{ padding: '4px' }}>
                    <button
                      type="button"
                      className={btnClass}
                      style={{ width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto', cursor: isAvail ? 'pointer' : 'not-allowed', opacity: isAvail || isSelected ? 1 : 0.3 }}
                      onClick={(e) => {
                        e.preventDefault();
                        if (!isAvail) return;
                        const slotObj = slotMap[dateVal];
                        if (slotObj) onSelect(slotObj);
                      }}
                    >
                      {day}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Main Component
const ServiceCardThemeV5 = ({
  data,
  selectService,
  isPreview,
  uploadDetails,
  updateState,
  confirmDetails,
  recaptcha,
  viewAvailableSlot,
  slugSelected,
  onResourceClick,
}) => {
  
  const eventData = data.eventData || data.eventDetails || {};
  const userDetails = data.userDetails || {};
  const resourceList = data.resource || data.resourceList || [];
  
  const businessLogo = isPreview 
    ? eventData.businessLogo 
    : eventData.businessLogo ? `${API_URL}auth/image/${eventData.businessLogo}` : calendar;
    
  const businessName = eventData.businessName || userDetails.userName || "Ananya Iyer";
  const displayTitle = eventData.serviceName || "Meet your morning differently.";

  // Social Media Extractor
  const { faceBook, instagram, linkedIn, youtube, personalWebsite } = userDetails;
  const hasSocials = faceBook || instagram || linkedIn || youtube || personalWebsite;

  // --- REVIEW RANKING & FALLBACK LOGIC ---
  const fallbackQuotes = [
    "A gentle structure that changes my whole day.",
    "Her cues are specific without ever feeling demanding.",
    "The one appointment I protect every week.",
    "I left feeling quieter in my own body.",
    "A transformative session."
  ];

  const rawReviews = data.reviewList || [];
  
  // Sort reviews by Top Rank (Satisfaction 5 -> 1) then by Date (Newest first)
  const sortedReviews = [...rawReviews].sort((a, b) => {
    if ((b.satisfaction || 0) !== (a.satisfaction || 0)) {
      return (b.satisfaction || 0) - (a.satisfaction || 0);
    }
    return (b.updateDate || 0) - (a.updateDate || 0);
  });

  const reviews = sortedReviews.length > 0 
    ? sortedReviews.map((rev, i) => {
        const isNotGiven = !rev.suggestion || rev.suggestion === "Not given" || rev.suggestion.trim() === "";
        return {
          ...rev,
          displaySuggestion: isNotGiven ? fallbackQuotes[i % fallbackQuotes.length] : rev.suggestion,
          displayName: rev.name || "Client",
          displaySource: rev.source || (rev.type === "review" ? "Client Review" : "Google")
        };
      })
    : [
        { displayName: "Tara K.", displaySource: "ClassPass", displaySuggestion: fallbackQuotes[0], satisfaction: 5, updateDate: Date.now() - 86400000 },
        { displayName: "Leela S.", displaySource: "Google", displaySuggestion: fallbackQuotes[1], satisfaction: 5, updateDate: Date.now() },
        { displayName: "Devika M.", displaySource: "ClassPass", displaySuggestion: fallbackQuotes[2], satisfaction: 5, updateDate: Date.now() },
        { displayName: "Priya V.", displaySource: "Featured review", displaySuggestion: fallbackQuotes[3], satisfaction: 5, updateDate: Date.now() }
      ];

  const avgRating = eventData.ratings ?? "4.9/5";

  // Slider Logic for 3-Column Grid
  const [reviewIndex, setReviewIndex] = useState(0);

  const handleNextReview = () => {
    if (reviewIndex + 3 < reviews.length) setReviewIndex(reviewIndex + 3);
    else setReviewIndex(0);
  };
  const handlePrevReview = () => {
    if (reviewIndex - 3 >= 0) setReviewIndex(reviewIndex - 3);
    else {
      const remainder = reviews.length % 3;
      setReviewIndex(reviews.length - (remainder === 0 ? 3 : remainder));
    }
  };

  const dummyServices = [
    { serviceName: "Tuesday - Flow", duration: 60, amount: 650, appointmentName: "INDIVIDUAL SESSION", description: "A personal practice shaped around your energy and experience.", timeLabel: "7:00 AM" },
    { serviceName: "Thursday - Mobility", duration: 60, amount: 650, appointmentName: "INDIVIDUAL SESSION", description: "A focused guided session with room for questions and adjustments.", timeLabel: "7:00 AM" },
    { serviceName: "Saturday - Long exhale", duration: 90, amount: 850, appointmentName: "SMALL GROUP CLASS", description: "A shared practice with a deliberately limited number of mats.", timeLabel: "8:30 AM" }
  ];
  const rawServiceList = data.service || data.serviceList || [];
  const serviceList = rawServiceList.length > 0 ? rawServiceList : dummyServices;

  const [chosenService, setChosenService] = useState(null);
  const [isPackageSelected, setIsPackageSelected] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedNoteOption, setSelectedNoteOption] = useState(""); // Track the dropdown selection
  const isMobile = window.screen.width < 768;

  useEffect(() => {
    if (!slugSelected || !serviceList.length) return;
    const index = serviceList.findIndex((s) => s.slug === slugSelected || String(s.id) === String(slugSelected));
    if (index !== -1) handleBookClick(serviceList[index], index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slugSelected, serviceList]);

  // FIX 2: close the modal automatically once a booking completes.
  // The free-booking flow never toggled isModalOpen (it's local state here),
  // so the modal stayed open after a free booking succeeded. The parent
  // (CalendarVcard) flips data.bookingSuccess to true on both the free and
  // paid success paths — this effect reacts to that signal.
  useEffect(() => {
    if (data.bookingSuccess) {
      setIsModalOpen(false);
    }
  }, [data.bookingSuccess]);

  const safeParse = (value) => {
    try {
      if (!value) return [];
      const p = JSON.parse(value);
      return typeof p === "string" ? JSON.parse(p) : p;
    } catch (e) { return []; }
  };

  const packageDays = safeParse(isPackageSelected?.packageDays);
  const timeSlots = safeParse(isPackageSelected?.timeSlots) || [];

  const getAllSlots = () => {
    if (!data.availableSlot?.length) return [];
    return data.availableSlot.filter((item) => {
      if (Object.keys(data?.activeIndexData || {}).length > 0) {
        const isToday = moment(data.activeIndexData.value, "YYYY/MM/DD");
        if (isToday.isSame(moment(), "day")) {
          const curTime = moment(new Date(), "HH:mm");
          const fromTime = moment(item.from, "HH:mm");
          return fromTime.isSameOrAfter(curTime);
        }
      }
      return true;
    });
  };

  const priceLabel = (svc) => (svc?.amount == 0 ? "Free" : `₹${svc?.amount}`);
  const selectedServiceObj = chosenService !== null ? serviceList[chosenService] : null;

  const handleBookClick = (item, ind) => {
    selectService(item);
    setChosenService(ind);
    setIsPackageSelected(item);
    updateState("bookingMode", "self");
    updateState("expertNote", "");
    updateState("bookingSuccess", false); // FIX 2: clear any stale success flag from a previous booking
    setIsModalOpen(true);
  };

  const handleResourceCardClick = (res) => {
    if (isPreview) return;
    if (typeof onResourceClick === "function") onResourceClick(res);
  };

  // Resets the form (name/email/phone/notes/step) and the reCAPTCHA when the
  // modal closes — otherwise stale values from this booking would still be
  // sitting there the next time the modal opens for another service.
  const closeModal = () => {
    setIsModalOpen(false);
    updateState("name", "");
    updateState("email", "");
    updateState("phoneNumber", "");
    updateState("description", "");
    updateState("detailsPage", false); // Reset back to step 1
    updateState("bookingSuccess", false); // FIX 2: keep state clean on manual close too
    setSelectedNoteOption(""); // Reset dropdown selection

    if (recaptcha?.current) {
      recaptcha.current.reset();
    }
  };

  // V3 Step Indicator
  const renderStepper = (activeStep) => (
    <div className="v5-bkStepBar">
      <div className={`v5-bkStep ${activeStep === 1 ? "active" : activeStep > 1 ? "done" : ""}`}>
        <div className="v5-bkStepCircle">{activeStep > 1 ? "✓" : "1"}</div>
        <span>Choose option</span>
      </div>
      <div className={`v5-bkStepLine ${activeStep > 1 ? "done" : ""}`} />
      <div className={`v5-bkStep ${activeStep === 2 ? "active" : activeStep > 2 ? "done" : ""}`}>
        <div className="v5-bkStepCircle">{activeStep > 2 ? "✓" : "2"}</div>
        <span>Provide details</span>
      </div>
      <div className={`v5-bkStepLine ${activeStep > 2 ? "done" : ""}`} />
      <div className={`v5-bkStep ${activeStep === 3 ? "active" : ""}`}>
        <div className="v5-bkStepCircle">3</div>
        <span>Confirmation</span>
      </div>
    </div>
  );

  // Modal Booking Flow perfectly mimicking V3 structure
  const renderBookingModal = () => {
    if (!isModalOpen || chosenService === null) return null;
    const isGroupPackage = ["group_package", "one_to_one_package"].includes(isPackageSelected?.appointmentName);
    
    return (
      <div className="v5-modal-overlay" onClick={closeModal}>
        <div className="v5-modal-content" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="v5-modal-close" onClick={closeModal}>×</button>
          
          {data.detailsPage ? (
            <form onSubmit={(e) => { e.preventDefault(); uploadDetails(e); }} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {renderStepper(2)}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px' }}>
                <h4 className="v5-section-title" style={{ margin: 0, fontSize: '24px' }}>Provide Details</h4>
                <button type="button" onClick={() => { recaptcha?.current?.reset(); updateState("detailsPage", false); }} className="v5-btn-outline" style={{ padding: '6px 12px', border: 'none' }}>
                  ← Back to options
                </button>
              </div>
              
              <div>
                <label style={{ display:'block', marginBottom:'8px', fontSize:'13px', fontWeight:600 }}>Name *</label>
                <input className="input-text-input" value={data.name} type="text" placeholder="Enter name" required onChange={(e) => updateState("name", e.target.value)} />
              </div>
              <div>
                <label style={{ display:'block', marginBottom:'8px', fontSize:'13px', fontWeight:600 }}>Email *</label>
                <input className="input-text-input" value={data.email} type="email" placeholder="Enter email" required onChange={(e) => updateState("email", e.target.value?.trim())} />
              </div>
              <div>
                <label style={{ display:'block', marginBottom:'8px', fontSize:'13px', fontWeight:600 }}>Phone Number *</label>
                <div style={{ marginTop: "4px" }}>
                  <PhoneInput
                    country={"in"}
                    containerClass="input-dark"
                    value={data.phoneNumber}
                    placeholder=""
                    countryCodeEditable={false}
                    onChange={(phone) => updateState("phoneNumber", phone)}
                    required
                  />
                </div>
              </div>
              <div>
                <label style={{ display:'block', marginBottom:'8px', fontSize:'13px', fontWeight:600 }}>Notes for preparation *</label>
                <select 
                  className="input-text-input" 
                  value={selectedNoteOption}
                  required
                  onChange={(e) => {
                    setSelectedNoteOption(e.target.value);
                    if (e.target.value !== "Other") {
                      updateState("description", e.target.value);
                    } else {
                      updateState("description", "");
                    }
                  }}
                  style={{ marginBottom: selectedNoteOption === "Other" ? '12px' : '0' }}
                >
                  <option value="" disabled>Select an option</option>
                  <option value="I'm looking for general relaxation and mindfulness.">I'm looking for general relaxation and mindfulness.</option>
                  <option value="I'm dealing with specific tension/tightness and need relief.">I'm dealing with specific tension/tightness and need relief.</option>
                  <option value="I'm a beginner looking for a gentle introduction.">I'm a beginner looking for a gentle introduction.</option>
                  <option value="Other">Other (I will type my own notes)</option>
                </select>

                {selectedNoteOption === "Other" && (
                  <textarea 
                    className="input-text-input" 
                    value={data.description} 
                    required={selectedNoteOption === "Other"} 
                    placeholder="Please specify your requirements or notes here..."
                    onChange={(e) => updateState("description", e.target.value)} 
                    style={{ minHeight: '100px', resize: 'vertical' }} 
                  />
                )}
              </div>
              
              <div style={{ margin: "8px 0" }}>
                <ReCAPTCHA size={isMobile ? "compact" : "normal"} ref={recaptcha} sitekey={REACT_APP_SITE_KEY} />
              </div>
              
              <button type="submit" className="v5-btn-primary" onClick={() => confirmDetails()} style={{ alignSelf: 'flex-start' }}>
                <FiCalendar size={16}/> Book {selectedServiceObj?.serviceName} - {priceLabel(selectedServiceObj)} <FiArrowRight size={16} />
              </button>
            </form>
          ) : (
            <div>
              {renderStepper(1)}
              
              {!isGroupPackage && (
                <>
                  <div style={{ marginBottom: '24px' }}>
                    <h2 className="v5-section-title" style={{ fontSize: '28px', marginBottom: '8px' }}>
                      <FiCalendar size={24} style={{ marginRight: '8px', verticalAlign: 'bottom' }}/> 
                      Schedule your session
                    </h2>
                    <p style={{ opacity: 0.8 }}>Choose how you'd like to book</p>
                  </div>
                  
                  <div className="v5-bkModeRow">
                    <div className={`v5-bkModeCard ${data.bookingMode === "self" ? "bkSelected" : ""}`} onClick={() => updateState("bookingMode", "self")}>
                      <div className="v5-bkModeRadio">{data.bookingMode === "self" && <div className="v5-bkModeRadioInner" />}</div>
                      <div className="v5-bkModeText">
                        <h5>I'll pick date & time</h5>
                        <p>Select from available slots</p>
                      </div>
                    </div>
                    
                    <div className={`v5-bkModeCard ${data.bookingMode === "expert" ? "bkSelected" : ""}`} onClick={() => updateState("bookingMode", "expert")}>
                      <div className="v5-bkModeRadio">{data.bookingMode === "expert" && <div className="v5-bkModeRadioInner" />}</div>
                      <div className="v5-bkModeText">
                        <h5>Let the expert choose</h5>
                        <p>Share your preference and expert will schedule</p>
                      </div>
                    </div>
                  </div>
                </>
              )}

              {data.bookingMode === "expert" && !isGroupPackage && (
                <div style={{ padding: '24px', background: 'rgba(74, 50, 36, 0.03)', border: '1px solid var(--v5-border)', borderRadius: '4px', marginBottom: '24px' }}>
                  <label style={{ display:'block', marginBottom:'12px', fontSize:'14px', fontWeight:700 }}>Share your preference / availability</label>
                  <textarea 
                    className="input-text-input"
                    placeholder="e.g. I'm available weekday evenings after 6 PM IST, prefer Thursday or Friday..."
                    value={data.expertNote || ""}
                    onChange={(e) => updateState("expertNote", e.target.value)}
                    style={{ minHeight: '100px', marginBottom: '12px' }}
                  />
                  <p style={{ fontSize: '12px', opacity: 0.7 }}>The expert will review your preference and send you a confirmed slot via email.</p>
                </div>
              )}

              {(data.bookingMode === "self" || isGroupPackage) && (
                <div className="v5-calTimeSplit">
                  <div className="v5-calSide">
                    <div className="v5-section-eyebrow" style={{ marginBottom: '16px' }}><FiCalendar size={13} style={{ marginRight:'4px' }}/> Select Date</div>
                    <MonthGridCalendar
                      slots={data.slots}
                      packageDays={isGroupPackage ? packageDays : []}
                      selectedValue={data.activeIndexData?.value}
                      onSelect={(slotObj) => {
                        const idx = data.slots?.findIndex((s) => s.value === slotObj.value);
                        viewAvailableSlot(idx ?? 0, slotObj, slotObj.available);
                      }}
                    />
                  </div>
                  
                  <div className="v5-timeSide">
                    <div className="v5-section-eyebrow" style={{ marginBottom: '16px' }}><FiClock size={13} style={{ marginRight:'4px' }}/> Available Time Slots</div>
                    
                    {data.activeIndexData?.value && (
                      <div className="v5-tzChip">
                        <FiClock size={11} /> {eventData.timeZone ? `All times shown in ${eventData.timeZone}` : "All times shown in IST"}
                      </div>
                    )}
                    
                    {!data.activeIndexData?.value ? (
                      <div style={{ padding: '40px 20px', textAlign: 'center', opacity: 0.5, border: '1px dashed var(--v5-border)', borderRadius: '8px' }}>
                        <FiCalendar size={32} style={{ marginBottom: '12px' }} />
                        <p style={{ fontSize: '13px' }}><strong>No date selected</strong><br/>Pick a date on the left to see available time slots</p>
                      </div>
                    ) : (
                      <div className="v5-twoColSlots">
                        {(isGroupPackage ? timeSlots : getAllSlots()).length > 0 ? (
                          (isGroupPackage ? timeSlots : getAllSlots()).map((item, i) => {
                            const fromStr = item.from ?? "";
                            const toStr = item.to ?? "";
                            const fmt = fromStr.includes("AM") || fromStr.includes("PM") ? "hh:mm A" : "HH:mm";
                            const displayFrom = moment(fromStr, fmt).isValid() ? moment(fromStr, fmt).format("h:mm A") : fromStr;
                            const displayTo = moment(toStr, fmt).isValid() ? moment(toStr, fmt).format("h:mm A") : toStr;
                            const isSelected = data.selectedSlot?.from === fromStr && data.selectedSlot?.to === toStr;
                            const isDisabled = item.slotAvailable === false;
                            
                            return (
                              <button
                                type="button"
                                key={i}
                                className={`timeSlotBtn ${isSelected ? 'tsActive' : ''}`}
                                style={{ padding: '10px', fontSize: '12px', cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.5 : 1 }}
                                onClick={(e) => { 
                                  e.preventDefault(); 
                                  if (!isDisabled) {
                                    updateState("slotActiveIndex", isGroupPackage ? i : getAllSlots().findIndex(s => s.from === item.from));
                                    updateState("selectedSlot", item);
                                  } 
                                }}
                              >
                                {displayFrom} – {displayTo}
                              </button>
                            );
                          })
                        ) : (
                          <p style={{ gridColumn: '1/-1', fontSize: '13px', opacity: 0.7 }}>No slots available for this date</p>
                        )}
                      </div>
                    )}

                    {!isGroupPackage && data.activeIndexData?.value && (
                       <div className="v5-sp-confirm-box">
                         <FiZap size={18} color="var(--v5-dark)" style={{ flexShrink: 0, marginTop: "2px" }} />
                         <div>
                           <h6>Instant confirmation</h6>
                           <p>Your session will be confirmed immediately after booking.</p>
                         </div>
                       </div>
                    )}
                  </div>
                </div>
              )}

              <div style={{ marginTop: '32px', display: 'flex', justifyContent: 'center' }}>
                <button 
                   type="button" 
                   className="v5-btn-primary" 
                   style={{ width: '100%', maxWidth: '400px' }}
                   onClick={() => isPreview ? () => {} : confirmDetails()}
                >
                  Continue to Details <FiArrowRight size={16} />
                </button>
              </div>

            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="v5-wrapper">
      <div className="v5-container">
        
        {/* Navigation */}
        <header className="v5-header">
          <div className="v5-logo-group">
            <span className="v5-logo-circle">
               {businessLogo && businessLogo !== calendar ? (
                 <img src={businessLogo} alt="Logo" />
               ) : (
                 businessName.charAt(0).toUpperCase() + (businessName.split(' ')[1]?.charAt(0).toUpperCase() || "")
               )}
            </span>
            <span>{businessName.toUpperCase()} · {eventData.specialisations?.split(',')[0]?.toUpperCase() || "MORNING YOGA"}</span>
          </div>
          <div>Practice with the light</div>
        </header>

        {/* Hero Area */}
        <section className="v5-hero">
          <div className="v5-hero-content">
            {eventData.sliderTextTwo && (
              <div className="v5-hero-eyebrow">{eventData.sliderTextTwo}</div>
            )}
            
            <h1 className="v5-hero-title">
               {eventData.sliderTextOne || displayTitle}
            </h1>
            
            <div className="v5-hero-desc" dangerouslySetInnerHTML={{__html: eventData.description || "Move, breathe, and begin with Ananya's grounded Vinyasa practice." }} />
            
            {/* FIX 1: scrollIntoView instead of offsetTop-based scrollTo — offsetTop
                is relative to the nearest positioned ancestor, not the page, so any
                positioned ancestor in the tree (e.g. the modal overlay) makes it
                resolve to 0 or the wrong offset. scrollIntoView always targets the
                real element regardless of ancestor positioning. */}
            <button
              type="button"
              className="v5-btn-primary"
              onClick={() =>
                document
                  .querySelector('.v5-services-wrapper')
                  ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
              }
            >
              Save your mat
            </button>

            {/* Render Social Media Links if Present */}
            {hasSocials && (
              <div className="v5-social-links">
                {personalWebsite && <a href={personalWebsite} target="_blank" rel="noreferrer" className="v5-social-icon"><FaGlobe /></a>}
                {faceBook && <a href={faceBook} target="_blank" rel="noreferrer" className="v5-social-icon"><FaFacebookF /></a>}
                {instagram && <a href={instagram} target="_blank" rel="noreferrer" className="v5-social-icon"><FaInstagram /></a>}
                {linkedIn && <a href={linkedIn} target="_blank" rel="noreferrer" className="v5-social-icon"><FaLinkedinIn /></a>}
                {youtube && <a href={youtube} target="_blank" rel="noreferrer" className="v5-social-icon"><FaYoutube /></a>}
              </div>
            )}
          </div>
          
          <div className="v5-hero-visual">
            {businessLogo && businessLogo !== calendar ? (
               <div className="v5-hero-image" style={{ backgroundImage: `url(${businessLogo})` }}></div>
            ) : (
               <div className="v5-hero-image" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=600')` }}></div>
            )}
          </div>
        </section>

        {/* Services Split Layout */}
        <div className="v5-services-wrapper">
          <div className="v5-services-left">
            <svg width="100" height="100" viewBox="0 0 100 100" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
              <text x="50%" y="50%" textAnchor="middle" dominantBaseline="central" fontSize="80" fontFamily="sans-serif">ॐ</text>
            </svg>
          </div>
          
          <div className="v5-services-right">
            <div className="v5-section-eyebrow">THIS WEEK'S PRACTICE</div>
            <h2 className="v5-section-title">Rise with intention.</h2>
            
            <div className="v5-services-list">
              {serviceList.map((item, ind) => {
                return (
                  <div key={ind} className="v5-service-item">
                    <div className="v5-service-header">
                      <div className="v5-service-name">{item.serviceName}</div>
                      <div className="v5-service-price">
                        {item.timeLabel || "Flexible"} · {priceLabel(item)}
                      </div>
                    </div>
                    <div className="v5-service-type">
                      {item.appointmentName?.replace(/_/g, ' ').toUpperCase() || "INDIVIDUAL SESSION"}
                    </div>
                    <div className="v5-service-desc" dangerouslySetInnerHTML={{__html: item.description}} />
                    <button type="button" className="v5-btn-outline" onClick={() => handleBookClick(item, ind)}>
                      {item.btnText || "Book this session →"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Ready-Made Resources Section */}
        {resourceList.length > 0 && (
          <section className="v5-grid-section" id="resources-section">
            <div className="v5-grid-header">
              <div>
                <div className="v5-section-eyebrow">READY-MADE RESOURCES</div>
                <h2 className="v5-section-title" style={{ margin: 0 }}>Get instant access</h2>
              </div>
            </div>
            <div className="v5-card-grid">
              {resourceList.map((res, ind) => {
                const isResourceFree = Number(res.amount) === 0;
                const fileCount = res.resourceFiles?.length || 0;
                
                return (
                  <div key={res.id} className="v5-card" onClick={() => handleResourceCardClick(res)} style={{ cursor: 'pointer' }}>
                    <div className="v5-section-eyebrow" style={{ opacity: 0.7, marginBottom: '8px' }}>
                      RESOURCE {ind + 1} {fileCount > 0 && `· ${fileCount} FILE${fileCount !== 1 ? 'S' : ''}`}
                    </div>
                    <h3 className="v5-service-name" style={{ fontSize: '20px', marginBottom: '8px' }}>{res.serviceName}</h3>
                    <div className="v5-service-price" style={{ marginBottom: '16px' }}>
                      {isResourceFree ? "Free" : `₹${res.amount}`}
                    </div>
                    <div className="v5-service-desc" dangerouslySetInnerHTML={{__html: res.description}} style={{ flex: 1 }} />
                    <button 
                      type="button"
                      className="v5-btn-outline" 
                      style={{ width: 'fit-content', marginTop: '16px' }}
                      onClick={(e) => { e.stopPropagation(); handleResourceCardClick(res); }}
                    >
                      {isResourceFree ? (
                        <>Get this resource <FiDownload size={14} /></>
                      ) : (
                        <>Buy now <FiLock size={12} /></>
                      )}
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* Testimonial Top Banner */}
        {reviews.length > 0 && (
          <div className="v5-testimonial-banner">
            <FiSun size={24} color="var(--v5-dark)" />
            <div className="v5-banner-quote">
              “{reviews[0]?.displaySuggestion}”
            </div>
            <div className="v5-banner-stars">
              {Array.from({ length: Math.round(reviews[0]?.satisfaction || 5) }).map((_, i) => (
                 <FaStar key={i} size={10} color="var(--v5-dark)" />
              ))}
            </div>
            <div className="v5-banner-author">
               {reviews[0]?.displayName} · {reviews[0]?.displaySource}
            </div>
          </div>
        )}

        {/* Client Notes (3 Column Grid with Slider Controls) */}
        {reviews.length > 0 && (
          <section className="v5-grid-section">
            <div className="v5-grid-header">
              <div>
                <div className="v5-section-eyebrow">CLIENT NOTES</div>
                <h2 className="v5-section-title" style={{ margin: 0 }}>What clients say about {businessName.split(' ')[0]}</h2>
              </div>
              <div style={{ fontSize: '12px', fontWeight: 600 }}>★★★★★ · {avgRating}</div>
            </div>
            
            <div className="v5-card-grid">
              {reviews.slice(reviewIndex, reviewIndex + 3).map((rev, idx) => (
                <div className="v5-card" key={idx}>
                  <div className="v5-review-stars">
                    {Array.from({ length: Math.round(rev.satisfaction || 5) }).map((_, i) => (
                       <FaStar key={i} size={14} color="var(--v5-dark)" />
                    ))}
                  </div>
                  <div className="v5-review-text">
                    “{rev.displaySuggestion}”
                  </div>
                  <div className="v5-review-author">
                    {rev.displayName} · {rev.displaySource}
                  </div>
                </div>
              ))}
            </div>

            {reviews.length > 3 && (
              <div className="v5-notes-controls">
                <div style={{ fontSize: '12px', opacity: 0.6, letterSpacing: '0.1em', fontWeight: 600 }}>
                  {Math.min(reviewIndex + 3, reviews.length)} OF {reviews.length}
                </div>
                <div>
                  <button type="button" className="v5-arrow-btn" onClick={handlePrevReview}>←</button>
                  <button type="button" className="v5-arrow-btn" onClick={handleNextReview}>→</button>
                </div>
              </div>
            )}
          </section>
        )}

        {/* About Provider */}
        <section className="v5-about-section">
          <div className="v5-section-eyebrow">MEET YOUR PROVIDER</div>
          <h3>{businessName}</h3>
          <p>{userDetails.profession?.professionName || "Ananya leads early-morning Vinyasa for people who want to start the day steadier."}</p>
          
          {eventData.specialisations && (
            <div className="v5-tag-container">
               {eventData.specialisations.split(',').map((spec, i) => (
                  <span key={i} className="v5-tag">{spec.trim()}</span>
               ))}
            </div>
          )}

          <div style={{ marginTop: '40px' }}>
            <svg width="120" height="30" viewBox="0 0 120 30" fill="none" xmlns="http://www.w3.org/2000/svg">
               <path d="M12.5 5 C10 12 15 20 18 20 C18 10 15 5 12.5 5 Z" fill="#e91e63" />
               <path d="M22 5 C24.5 12 19 20 16 20 C16 10 19 5 22 5 Z" fill="#e91e63" opacity="0.8" />
               <text x="32" y="21" fontFamily="Inter" fontWeight="700" fontSize="18" fill="var(--v5-dark)">Yuukke</text>
            </svg>
          </div>
        </section>
        
        {/* Render Modal last so it overlays everything */}
        {renderBookingModal()}
      </div>
    </div>
  );
};

export default ServiceCardThemeV5;